import React, { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHeader, SectionTitle, EditorialButton, StatusDot } from "@/lib/editorial";
import { CHROME_IMAGES } from "@/data/mockData";
import { useAgents } from "@/data/agentsStore";
import { postsApi } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  UploadCloud, ChevronLeft, ChevronRight, Plus, Loader2,
  Sparkles, Trash2, Check, X, Pencil,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  scheduled: "border-[var(--blue)] text-[var(--blue)] bg-blue-50",
  draft:     "border-amber-400 text-amber-700 bg-amber-50",
  posted:    "border-emerald-400 text-emerald-700 bg-emerald-50",
  failed:    "border-rose-400 text-rose-700 bg-rose-50",
};

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Return the Monday of the week containing `date`
function weekStart(date) {
  const d = new Date(date);
  const day = d.getUTCDay(); // 0 = Sun
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  d.setUTCHours(0, 0, 0, 0);
  return d;
}

function fmtDate(d) {
  return d.toISOString().slice(0, 10);
}

export default function PostingHub() {
  const qc = useQueryClient();
  const agents = useAgents();
  const fileInputRef = useRef(null);

  // Compose form state
  const [selected, setSelected] = useState(new Set());
  const [sameCaption, setSameCaption] = useState(true);
  const [caption, setCaption] = useState("");
  const [schedDate, setSchedDate] = useState("");
  const [schedTime, setSchedTime] = useState("09:00");
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [topic, setTopic] = useState("");

  // Calendar week offset (0 = current week)
  const [weekOffset, setWeekOffset] = useState(0);
  const monday = weekStart(new Date());
  monday.setUTCDate(monday.getUTCDate() + weekOffset * 7);
  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setUTCDate(d.getUTCDate() + i);
    return d;
  });
  const weekLabel = `${weekDates[0].toUTCString().slice(5, 11)} — ${weekDates[6].toUTCString().slice(5, 11)}, ${weekDates[0].getUTCFullYear()}`;

  const { data: postsData = [], refetch: refetchPosts } = useQuery({
    queryKey: ["posts"],
    queryFn: postsApi.list,
    refetchInterval: 30_000,
  });

  // Delete post
  const deletePost = useMutation({
    mutationFn: (id) => postsApi.remove(id),
    onSuccess: () => { toast("Post deleted."); qc.invalidateQueries({ queryKey: ["posts"] }); },
    onError: (e) => toast.error(e.message),
  });

  // Create draft + schedule + upload media
  const createPost = useMutation({
    mutationFn: async () => {
      if (selected.size === 0) throw new Error("Select at least one account.");
      const scheduledAt = schedDate ? `${schedDate}T${schedTime}:00` : null;
      // Create one post per selected account when sameCaption = false, else one shared
      const agentList = [...selected];
      const results = [];
      for (const agentId of agentList) {
        const res = await postsApi.create({ agentId, caption, scheduledAt });
        const postId = res.id;
        if (mediaFile && postId) {
          try { await postsApi.uploadMedia(postId, mediaFile); } catch (e) { toast.error(`Media upload failed: ${e.message}`); }
        }
        // If not same caption, generate individual AI caption
        if (!sameCaption && topic) {
          try {
            const gen = await postsApi.generateCaption(postId, { topic, agentIds: [agentId] });
            const c = gen.captions?.[0]?.caption;
            if (c) await postsApi.update(postId, { caption: c });
          } catch {}
        }
        results.push(postId);
      }
      return results;
    },
    onSuccess: () => {
      toast.success("Post(s) saved.");
      setCaption("");
      setMediaFile(null);
      setMediaPreview(null);
      setSchedDate("");
      qc.invalidateQueries({ queryKey: ["posts"] });
    },
    onError: (e) => toast.error(e.message),
  });

  async function handleGenerate() {
    if (selected.size === 0 && agents.length === 0) return;
    setGenerating(true);
    try {
      // Use a temp draft post or just call generate with topic
      const agentIds = selected.size > 0 ? [...selected] : [agents[0]?.id].filter(Boolean);
      // Create a temp post to attach caption generation to
      const res = await postsApi.create({ agentId: agentIds[0], caption: topic || "LinkedIn post", scheduledAt: null });
      const postId = res.id;
      const gen = await postsApi.generateCaption(postId, { topic: topic || undefined, agentIds });
      // Show first caption in textarea
      const first = gen.captions?.find((c) => !c.error);
      if (first) {
        setCaption(first.caption);
        toast.success(`Caption generated for ${first.agentName}`);
      }
      // Delete the temp post
      await postsApi.remove(postId);
      qc.invalidateQueries({ queryKey: ["posts"] });
    } catch (e) {
      toast.error(e.message || "Generation failed");
    } finally {
      setGenerating(false);
    }
  }

  function handleFileChange(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setMediaFile(f);
    if (f.type.startsWith("image/")) {
      setMediaPreview(URL.createObjectURL(f));
    } else {
      setMediaPreview(null);
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) { setMediaFile(f); if (f.type.startsWith("image/")) setMediaPreview(URL.createObjectURL(f)); }
  }

  return (
    <div className="text-[var(--ink)]">
      <div className="relative bg-[var(--ink)] text-white overflow-hidden">
        <img src={CHROME_IMAGES.headerDecor} alt="" className="chrome-img absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--ink)] via-[var(--ink)]/85 to-transparent" />
        <div className="relative px-8 lg:px-14 py-16">
          <PageHeader
            eyebrow="Posting hub"
            tape="Distribute"
            rest="content"
            sub="Schedule, generate, and distribute LinkedIn posts across all connected accounts — with per-account persona-aware captions."
            dark
            testId="posting-title"
          />
        </div>
      </div>

      {/* Queue */}
      <section className="px-8 lg:px-14 py-12 border-b border-[var(--rule-light)]">
        <SectionTitle eyebrow="Section 01">Content queue</SectionTitle>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] min-w-[900px]" data-testid="posting-queue">
            <thead>
              <tr className="text-left border-b border-[var(--ink)]">
                <Th>Status</Th><Th>Account</Th><Th>Scheduled for</Th>
                <Th>Caption preview</Th><Th>Media</Th><Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {postsData.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-[var(--muted-light)] italic font-display">No posts yet. Compose one below.</td></tr>
              )}
              {postsData.map((p) => (
                <PostRow key={p.id} post={p} onDelete={() => deletePost.mutate(p.id)} onRefresh={refetchPosts} />
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Compose */}
      <section className="px-8 lg:px-14 py-12 grid grid-cols-1 xl:grid-cols-3 gap-8 border-b border-[var(--rule-light)] bg-[var(--paper-2)]">
        <div className="xl:col-span-2">
          <SectionTitle eyebrow="Section 02">Compose</SectionTitle>

          {/* Media upload */}
          <div
            className="border-2 border-dashed border-[var(--ink)]/30 p-8 bg-white text-center cursor-pointer hover:border-[var(--blue)] transition-colors relative"
            data-testid="posting-upload-dropzone"
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            <input ref={fileInputRef} type="file" accept="image/*,video/*,.pdf" className="hidden" onChange={handleFileChange} />
            {mediaPreview ? (
              <img src={mediaPreview} alt="Preview" className="max-h-48 mx-auto object-contain" />
            ) : mediaFile ? (
              <div className="text-[var(--blue)] font-display text-[15px]">📎 {mediaFile.name}</div>
            ) : (
              <>
                <UploadCloud className="w-7 h-7 mx-auto text-[var(--blue)]" />
                <div className="font-display italic text-2xl mt-2">Drop media here</div>
                <div className="text-[12px] text-[var(--muted-light)] mt-1">or click to browse · JPG, PNG, MP4, MOV, PDF</div>
              </>
            )}
            {(mediaFile) && (
              <button
                onClick={(e) => { e.stopPropagation(); setMediaFile(null); setMediaPreview(null); }}
                className="absolute top-2 right-2 text-[var(--muted-light)] hover:text-rose-600"
              ><X className="w-4 h-4" /></button>
            )}
          </div>

          {/* Topic hint for AI */}
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--muted-light)] mb-1">Topic / hint for AI generation (optional)</div>
            <input
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g. 'productivity tips for founders' or paste a URL"
              className="w-full border border-[var(--rule-light)] focus:border-[var(--blue)] outline-none px-3 py-2 text-[13px] bg-white"
            />
          </div>

          {/* Caption */}
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--muted-light)] mb-1">Caption</div>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Write a caption, or generate one automatically…"
              rows={6}
              className="w-full border border-[var(--rule-light)] focus:border-[var(--blue)] outline-none p-3 text-[14px] leading-relaxed bg-white"
              data-testid="posting-caption"
            />
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <EditorialButton variant="secondary" onClick={handleGenerate} disabled={generating} data-testid="posting-generate">
                {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {generating ? "Generating…" : "Generate caption with AI"}
              </EditorialButton>
              <span className="text-[11px] font-mono text-[var(--muted-light)]">{caption.length} / 3000</span>
            </div>
          </div>
        </div>

        {/* Distribution + schedule */}
        <div>
          <SectionTitle eyebrow="Section 03">Distribution</SectionTitle>

          <div className="bg-white border border-[var(--rule-light)] p-5 space-y-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--muted-light)]">Post to accounts</div>
            {agents.map((a) => (
              <label key={a.id} className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={selected.has(a.id)}
                  onCheckedChange={(v) => {
                    const next = new Set(selected);
                    v ? next.add(a.id) : next.delete(a.id);
                    setSelected(next);
                  }}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <StatusDot active={a.active} />
                    <span className="font-display text-[15px]">{a.name}</span>
                  </div>
                  {a.persona && (
                    <p className="text-[11px] text-[var(--muted-light)] mt-0.5 leading-snug line-clamp-2">{a.persona}</p>
                  )}
                </div>
              </label>
            ))}

            <div className="flex items-center justify-between pt-3 border-t border-[var(--rule-light)]">
              <span className="text-[11px] uppercase tracking-[0.18em]">Same caption for all accounts</span>
              <Switch checked={sameCaption} onCheckedChange={setSameCaption} data-testid="posting-same-caption" />
            </div>
            {!sameCaption && (
              <p className="text-[11px] text-[var(--muted-light)]">
                AI will generate a separate persona-aware caption for each selected account.
              </p>
            )}
          </div>

          <div className="bg-white border border-[var(--rule-light)] p-5 mt-4 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--muted-light)]">Schedule (UTC)</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date</Label>
                <input type="date" value={schedDate} onChange={(e) => setSchedDate(e.target.value)}
                  className="w-full border-b border-[var(--ink)]/30 bg-transparent py-2 font-mono text-[13px]" />
              </div>
              <div>
                <Label>Time</Label>
                <input type="time" value={schedTime} onChange={(e) => setSchedTime(e.target.value)}
                  className="w-full border-b border-[var(--ink)]/30 bg-transparent py-2 font-mono text-[13px]" />
              </div>
            </div>
            {!schedDate && <p className="text-[11px] text-[var(--muted-light)]">Leave blank to save as draft only.</p>}
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <EditorialButton onClick={() => createPost.mutate()} disabled={createPost.isPending} data-testid="posting-schedule">
              {createPost.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              {schedDate ? "Save & schedule" : "Save as draft"}
            </EditorialButton>
          </div>
        </div>
      </section>

      {/* Calendar */}
      <section className="px-8 lg:px-14 py-12 border-b border-[var(--rule-light)]">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <SectionTitle eyebrow="Section 04" className="mb-0">Weekly calendar</SectionTitle>
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em]">
            <button onClick={() => setWeekOffset((w) => w - 1)} className="flex items-center gap-1 hover:text-[var(--blue)]">
              <ChevronLeft className="w-3.5 h-3.5" /> Prev
            </button>
            <span className="font-mono">{weekLabel}</span>
            <button onClick={() => setWeekOffset((w) => w + 1)} className="flex items-center gap-1 hover:text-[var(--blue)]">
              Next <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-2">
          {weekDates.map((d, i) => {
            const iso = fmtDate(d);
            const dayPosts = postsData.filter((p) => p.scheduledAt?.slice(0, 10) === iso);
            return (
              <div key={i} className="border border-[var(--rule-light)] min-h-[140px] bg-white">
                <div className="px-2 py-1.5 border-b border-[var(--rule-light)] flex items-center justify-between">
                  <span className="text-[9px] uppercase tracking-[0.22em] text-[var(--muted-light)]">{DAYS[i]}</span>
                  <span className="font-mono text-[10px]">{d.getUTCDate().toString().padStart(2, "0")}</span>
                </div>
                <div className="p-1.5 space-y-1.5">
                  {dayPosts.map((p) => (
                    <div key={p.id} className="border border-[var(--blue)]/30 bg-blue-50 p-1.5 text-[10.5px]">
                      <div className="font-mono text-[9px] text-[var(--blue)] truncate">{p.agentName?.split(" ")[0]}</div>
                      <div className="text-[var(--ink)]/80 truncate">{p.caption?.slice(0, 35)}{p.caption?.length > 35 ? "…" : ""}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Account persona cards */}
      <section className="px-8 lg:px-14 py-12">
        <SectionTitle eyebrow="Section 05">Account personas</SectionTitle>
        <p className="text-[13px] text-[var(--muted-light)] mb-6">
          Define each account's writing style and personality. The AI uses this when generating captions.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {agents.map((a) => <PersonaCard key={a.id} agent={a} />)}
        </div>
      </section>
    </div>
  );
}

// --- Post row with inline edit ---
function PostRow({ post: p, onDelete, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [captionDraft, setCaptionDraft] = useState(p.caption);

  async function saveCaption() {
    try {
      await postsApi.update(p.id, { caption: captionDraft });
      toast("Caption updated.");
      onRefresh();
      setEditing(false);
    } catch (e) { toast.error(e.message); }
  }

  return (
    <tr className="border-b border-[var(--rule-light)]">
      <td className="py-3 pr-4">
        <span className={`text-[10px] uppercase tracking-wider border px-1.5 leading-[18px] font-mono ${STATUS_COLOR[p.status] || STATUS_COLOR["draft"]}`}>
          {p.status}
        </span>
      </td>
      <td className="py-3 pr-4 font-display text-[15px]">{p.agentName}</td>
      <td className="py-3 pr-4 font-mono text-[12px]">{p.scheduledAt?.slice(0, 16) ?? "—"}</td>
      <td className="py-3 pr-4 max-w-[300px]">
        {editing ? (
          <div className="flex items-start gap-1">
            <textarea rows={2} value={captionDraft} onChange={(e) => setCaptionDraft(e.target.value)}
              className="flex-1 border border-[var(--blue)] text-[12px] p-1 outline-none" autoFocus />
            <button onClick={saveCaption} className="text-emerald-500 hover:text-emerald-700"><Check className="w-4 h-4" /></button>
            <button onClick={() => setEditing(false)} className="text-[var(--muted-light)]"><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <div className="truncate text-[var(--muted-light)] text-[12.5px]">{p.caption}</div>
        )}
      </td>
      <td className="py-3 pr-4">
        {p.mediaUrl
          ? <a href={p.mediaUrl} target="_blank" rel="noreferrer" className="font-mono text-[11px] text-[var(--blue)] hover:underline">View</a>
          : <span className="text-[var(--muted-light)] italic text-[12px]">—</span>}
      </td>
      <td className="py-3 pr-4">
        <div className="flex gap-3">
          <button onClick={() => { setCaptionDraft(p.caption); setEditing(true); }}
            className="text-[11px] uppercase tracking-[0.18em] text-[var(--ink)] hover:text-[var(--blue)] flex items-center gap-1">
            <Pencil className="w-3 h-3" /> Edit
          </button>
          <button onClick={onDelete}
            className="text-[11px] uppercase tracking-[0.18em] text-rose-600 hover:text-rose-800 flex items-center gap-1">
            <Trash2 className="w-3 h-3" /> Delete
          </button>
        </div>
      </td>
    </tr>
  );
}

// --- Persona card with inline editing and save ---
function PersonaCard({ agent: a }) {
  const [draft, setDraft] = useState(a.persona || "");
  const [saving, setSaving] = useState(false);
  const dirty = draft !== (a.persona || "");

  async function save() {
    setSaving(true);
    try {
      await postsApi.savePersona(a.id, draft, a.name);
      toast(`Persona saved for ${a.name}.`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="border border-[var(--rule-light)] bg-white p-5">
      <div className="flex items-center gap-2 mb-3">
        <StatusDot active={a.active} />
        <div className="font-display text-[18px]">{a.name}</div>
      </div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--muted-light)] mb-1">Persona / writing style</div>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={4}
        placeholder="Describe this account's voice, tone, and expertise…"
        className="w-full text-[12.5px] border border-[var(--rule-light)] focus:border-[var(--blue)] outline-none p-2 leading-relaxed"
      />
      {dirty && (
        <button onClick={save} disabled={saving}
          className="mt-2 w-full bg-[var(--ink)] hover:bg-[var(--blue)] disabled:opacity-50 text-white text-[10px] uppercase tracking-[0.22em] py-2 flex items-center justify-center gap-2 transition-colors">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
          {saving ? "Saving…" : "Save persona"}
        </button>
      )}
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left text-[10px] uppercase tracking-[0.22em] text-[var(--muted-light)] py-3 pr-4">{children}</th>;
}

function Label({ children }) {
  return <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--muted-light)] mb-1">{children}</div>;
}
